// Section « Historique des performances » de la fiche appareil — pleine
// largeur, juste après la grille de disponibilité : celle-ci répond « la
// machine tournait-elle ? », celle-ci répond « à quel point ? ».
//
// Deuxième consommateur de `heartbeat_trends` (cf. GET /api/devices/{id}/trends
// et CLAUDE.md « Disponibilité heure par heure »). Mêmes conventions : fenêtre
// mémorisée en localStorage et revalidée contre les options offertes, seaux en
// UTC convertis côté client dans le fuseau du navigateur.
//
// Un seul SVG fait main (pas de dépendance de graphique) : bande min–max,
// ligne de moyenne, axes semi-annotés. Les couleurs passent par les variables
// CSS de thème (rgb(var(--c-...))) comme dans SparklineChart — jamais de hex
// en dur, pour rester correct en clair comme en sombre.

import { useEffect, useMemo, useState } from 'react';
import { LineChart } from 'lucide-react';
import { api } from '../lib/api.jsx';

const RANGES = [
  { days: 7, label: '7 jours' },
  { days: 30, label: '30 jours' },
  { days: 90, label: '90 jours' },
  { days: 365, label: '1 an' },
];

const DAYS_KEY = 'mh_perf_days';
const DEFAULT_DAYS = 7;

// Revalidée contre les options offertes — même garde que `readDays` dans
// UptimeHeatmap : une valeur restée d'une version qui proposait d'autres
// fenêtres partirait sinon telle quelle dans la requête.
function readDays() {
  const stored = Number(localStorage.getItem(DAYS_KEY));
  return RANGES.some((r) => r.days === stored) ? stored : DEFAULT_DAYS;
}

const TABS = [
  { key: 'cpu', label: 'CPU', unit: '%' },
  { key: 'mem', label: 'Mémoire', unit: '%' },
  { key: 'gpu', label: 'GPU', unit: '%' },
  { key: 'temp', label: 'Températures', unit: '°C' },
];

const W = 1000;
const H = 260;
const PAD = { t: 16, r: 20, b: 34, l: 46 };

const fmtHourLong = (ms) =>
  new Date(ms).toLocaleString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

const fmtTick = (ms, days) =>
  new Date(ms).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    ...(days <= 7 ? { hour: '2-digit' } : {}),
  });

// Un point n'a QUE les métriques qu'il porte : `cpu`/`mem`/`gpu` sont
// toujours présents (0 est une valeur légitime, cf. CLAUDE.md), `cpuTemp`/
// `gpuTemp` valent `null` quand aucun capteur n'a répondu cette heure-là.
function seriesFor(points, tab) {
  if (tab === 'temp') return null; // traité à part (deux sous-séries)
  return points.map((p) => ({ ms: new Date(p.hourStart).getTime(), v: p[tab], samples: p.samples }));
}

// Découpe une série en tronçons contigus : un trou (bucket manquant, ou
// métrique non mesurée cette heure-là) casse la ligne au lieu de la relier —
// une bande min–max tracée par-dessus une heure sans donnée mentirait sur ce
// qui a été mesuré.
function toSegments(entries, bucketMs) {
  const segments = [];
  let current = [];
  for (const e of entries) {
    if (e.v == null) {
      if (current.length) segments.push(current);
      current = [];
      continue;
    }
    if (current.length) {
      const prevMs = current[current.length - 1].ms;
      if (e.ms - prevMs > bucketMs * 1.5) {
        segments.push(current);
        current = [];
      }
    }
    current.push(e);
  }
  if (current.length) segments.push(current);
  return segments;
}

function Chart({ points, bucketHours, days, tab, unit }) {
  const [hover, setHover] = useState(null); // { ms, x }
  const bucketMs = bucketHours * 3600000;
  const pw = W - PAD.l - PAD.r;
  const ph = H - PAD.t - PAD.b;

  const startMs = points.length ? new Date(points[0].hourStart).getTime() : 0;
  const endMs = points.length ? new Date(points[points.length - 1].hourStart).getTime() : 1;
  const span = Math.max(endMs - startMs, bucketMs);
  const xp = (ms) => PAD.l + ((ms - startMs) / span) * pw;

  // Séries à tracer : une seule pour CPU/Mémoire/GPU, deux (nuancées) pour
  // Températures — pas de teinte dédiée par métrique (contrainte tokens
  // sémantiques uniquement), la distinction se fait par intensité.
  const rawSeries =
    tab === 'temp'
      ? [
          { key: 'cpuTemp', label: 'CPU', color: 'rgb(var(--c-fg-2))', fill: 'rgb(var(--c-fg-2))' },
          { key: 'gpuTemp', label: 'GPU', color: 'rgb(var(--c-muted))', fill: 'rgb(var(--c-muted))' },
        ].map((s) => ({
          ...s,
          entries: points.map((p) => ({
            ms: new Date(p.hourStart).getTime(),
            v: p[s.key] ? p[s.key].avg : null,
            min: p[s.key] ? p[s.key].min : null,
            max: p[s.key] ? p[s.key].max : null,
            samples: p.samples,
          })),
        })).filter((s) => s.entries.some((e) => e.v != null))
      : [
          {
            key: tab,
            label: TABS.find((t) => t.key === tab)?.label,
            color: 'rgb(var(--c-fg-2))',
            fill: 'rgb(var(--c-fg-2))',
            entries: points.map((p) => ({
              ms: new Date(p.hourStart).getTime(),
              v: p[tab] ? p[tab].avg : null,
              min: p[tab] ? p[tab].min : null,
              max: p[tab] ? p[tab].max : null,
              samples: p.samples,
            })),
          },
        ];

  // Échelle Y : fixe 0–100 % pour les métriques de charge, auto-ajustée pour
  // les températures (aucun seuil universel commun CPU/GPU).
  let yMin = 0;
  let yMax = 100;
  if (tab === 'temp') {
    const vals = rawSeries.flatMap((s) => s.entries.flatMap((e) => [e.min, e.max]).filter((v) => v != null));
    if (vals.length) {
      yMin = Math.max(0, Math.floor(Math.min(...vals) / 5) * 5 - 5);
      yMax = Math.ceil(Math.max(...vals) / 5) * 5 + 5;
    } else {
      yMin = 0;
      yMax = 100;
    }
  }
  const yp = (v) => PAD.t + (1 - (Math.min(Math.max(v, yMin), yMax) - yMin) / (yMax - yMin)) * ph;

  const grid = 'rgb(var(--c-rule))';
  const muted = 'rgb(var(--c-muted-3))';

  const yTicks =
    tab === 'temp'
      ? Array.from({ length: 5 }, (_, i) => Math.round(yMin + ((yMax - yMin) * i) / 4))
      : [0, 25, 50, 75, 100];

  // ~6 graduations X réparties sur la plage réellement couverte.
  const xTickCount = 6;
  const xTicks = Array.from({ length: xTickCount + 1 }, (_, i) => startMs + (span * i) / xTickCount);

  const handleMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const ms = startMs + ((px - PAD.l) / pw) * span;
    // Point le plus proche en temps, tous seaux confondus.
    let best = null;
    let bestDist = Infinity;
    for (const p of points) {
      const pms = new Date(p.hourStart).getTime();
      const dist = Math.abs(pms - ms);
      if (dist < bestDist) {
        bestDist = dist;
        best = p;
      }
    }
    if (best && bestDist <= bucketMs) setHover(best);
    else setHover(null);
  };

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-[240px] block"
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
      >
        {yTicks.map((t) => (
          <g key={`y${t}`}>
            <line x1={PAD.l} y1={yp(t)} x2={PAD.l + pw} y2={yp(t)} stroke={grid} strokeWidth={1} strokeDasharray={t === yMin ? 'none' : '3,4'} />
            <text x={PAD.l - 6} y={yp(t) + 3} textAnchor="end" fontSize={10} fill={muted} fontFamily="monospace">
              {`${t}${unit}`}
            </text>
          </g>
        ))}

        {xTicks.map((ms, i) => (
          <text key={`x${i}`} x={xp(ms)} y={H - 8} textAnchor="middle" fontSize={9.5} fill={muted} fontFamily="monospace">
            {fmtTick(ms, days)}
          </text>
        ))}

        {rawSeries.map((s) =>
          toSegments(s.entries, bucketMs).map((seg, si) => {
            if (seg.length < 2) {
              const e = seg[0];
              return (
                <circle key={`${s.key}-pt-${si}`} cx={xp(e.ms)} cy={yp(e.v)} r={2.5} fill={s.color} />
              );
            }
            const bandTop = seg.map((e) => `${xp(e.ms).toFixed(1)},${yp(e.max).toFixed(1)}`).join(' L');
            const bandBottom = seg
              .slice()
              .reverse()
              .map((e) => `${xp(e.ms).toFixed(1)},${yp(e.min).toFixed(1)}`)
              .join(' L');
            const band = `M${bandTop} L${bandBottom} Z`;
            const line = seg
              .map((e, i) => `${i === 0 ? 'M' : 'L'}${xp(e.ms).toFixed(1)},${yp(e.v).toFixed(1)}`)
              .join(' ');
            return (
              <g key={`${s.key}-seg-${si}`}>
                <path d={band} fill={s.fill} opacity={0.12} stroke="none" />
                <path d={line} fill="none" stroke={s.color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
              </g>
            );
          })
        )}

        {hover && (() => {
          const ms = new Date(hover.hourStart).getTime();
          const x = xp(ms);
          return <line x1={x} y1={PAD.t} x2={x} y2={PAD.t + ph} stroke={muted} strokeWidth={1} strokeDasharray="2,3" />;
        })()}

        <line x1={PAD.l} y1={PAD.t + ph} x2={PAD.l + pw} y2={PAD.t + ph} stroke={grid} strokeWidth={1} />
      </svg>

      {tab === 'temp' && rawSeries.length > 1 && (
        <div className="absolute top-1 right-1 flex gap-3 text-[10px] text-muted-2">
          {rawSeries.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1">
              <span className="w-[8px] h-[8px] rounded-full inline-block" style={{ background: s.color }} />
              {s.label}
            </span>
          ))}
        </div>
      )}

      {hover && (
        <div
          className="absolute top-2 pointer-events-none bg-card border border-rule rounded-lg px-2.5 py-2 text-[11px] shadow-lg"
          style={{
            left: `clamp(4px, ${(xp(new Date(hover.hourStart).getTime()) / W) * 100}%, calc(100% - 160px))`,
          }}
        >
          <div className="text-fg-2 font-semibold mb-1">{fmtHourLong(new Date(hover.hourStart).getTime())}</div>
          {tab === 'temp' ? (
            <>
              {['cpuTemp', 'gpuTemp'].map((k) =>
                hover[k] ? (
                  <div key={k} className="text-muted-2">
                    {k === 'cpuTemp' ? 'CPU' : 'GPU'} : {hover[k].min.toFixed(1)} / {hover[k].avg.toFixed(1)} /{' '}
                    {hover[k].max.toFixed(1)} °C (min/moy/max)
                  </div>
                ) : (
                  <div key={k} className="text-muted-3">
                    {k === 'cpuTemp' ? 'CPU' : 'GPU'} : pas de capteur
                  </div>
                )
              )}
            </>
          ) : (
            hover[tab] && (
              <div className="text-muted-2">
                {hover[tab].min.toFixed(1)} / {hover[tab].avg.toFixed(1)} / {hover[tab].max.toFixed(1)} {unit}
                <span className="text-muted-3"> (min/moy/max)</span>
              </div>
            )
          )}
          <div className="text-muted-3 mt-0.5">{hover.samples} signal{hover.samples > 1 ? 's' : ''}</div>
        </div>
      )}
    </div>
  );
}

export function PerformanceHistory({ agentId, sectionCls, sTitleCls }) {
  const [days, setDays] = useState(readDays);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('cpu');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/devices/${agentId}/trends?days=${days}`).then((r) => {
      if (cancelled) return;
      setData(r);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [agentId, days]);

  const points = data?.points || [];

  // Onglets GPU / Températures masqués quand la série est entièrement vide —
  // même convention que le sparkline GPU existant plus haut sur la fiche
  // (`hbs.some((hb) => (hb.gpuPercent || 0) > 0)`) : 0 % de charge GPU sur
  // toute la fenêtre veut dire « pas de GPU surveillé », pas « GPU au repos ».
  const hasGpu = useMemo(() => points.some((p) => (p.gpu?.max || 0) > 0), [points]);
  const hasTemp = useMemo(() => points.some((p) => p.cpuTemp || p.gpuTemp), [points]);

  const visibleTabs = TABS.filter((t) => (t.key === 'gpu' ? hasGpu : t.key === 'temp' ? hasTemp : true));
  useEffect(() => {
    if (!visibleTabs.some((t) => t.key === tab)) setTab(visibleTabs[0]?.key || 'cpu');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGpu, hasTemp]);

  const activeTab = visibleTabs.find((t) => t.key === tab) || visibleTabs[0];

  return (
    <div className={sectionCls}>
      <div className={`${sTitleCls} justify-between`}>
        <span className="inline-flex items-center gap-2">
          <LineChart size={16} strokeWidth={1.75} /> Historique des performances
        </span>
        <select
          value={days}
          onChange={(e) => {
            const next = Number(e.target.value);
            localStorage.setItem(DAYS_KEY, String(next));
            setDays(next);
          }}
          className="bg-sidebar border border-rule rounded-lg px-2 py-1 text-muted text-[11px] outline-none font-normal"
        >
          {RANGES.map((r) => (
            <option key={r.days} value={r.days}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-muted-2 text-xs">Chargement...</div>
      ) : points.length === 0 ? (
        <div className="text-muted-2 text-xs py-6 text-center">
          Pas encore d'historique : les agrégats sont calculés chaque nuit.
        </div>
      ) : (
        <>
          <div className="flex gap-1 mb-3 border-b border-rule">
            {visibleTabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-3 py-1.5 text-[11.5px] rounded-t-lg border-b-2 -mb-px transition-colors ${
                  activeTab?.key === t.key
                    ? 'border-fg-2 text-fg-2 font-semibold'
                    : 'border-transparent text-muted-2 hover:text-fg-2'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {activeTab && (
            <Chart points={points} bucketHours={data.bucketHours} days={days} tab={activeTab.key} unit={activeTab.unit} />
          )}
        </>
      )}
    </div>
  );
}
